import { v4 as uuidv4 } from 'uuid';
import { DemoFan } from '../../datas/demoFan';
import { GenerationResponse } from '../../services/generationsApi';
import { extractTitleFromLyrics } from '../../utils/lyricsTitleExtractor';

interface AutoSaveImageParams {
  imageVersion: 1 | 2;
  imageUrl: string;
  userInput: string;
  fanData: DemoFan;
  saveCreation: (variables: any, options?: any) => void;
  saveCreationAsync?: (variables: any) => Promise<any>;
  savedImageCreationId1Ref?: React.MutableRefObject<string | null>;
  savedImageCreationId2Ref?: React.MutableRefObject<string | null>;
  onSaveComplete?: (imageVersion: 1 | 2, savedId: string) => void;
}

export const autoSaveImage = ({
  imageVersion,
  imageUrl,
  userInput,
  fanData,
  saveCreation,
  saveCreationAsync,
  savedImageCreationId1Ref,
  savedImageCreationId2Ref,
  onSaveComplete,
}: AutoSaveImageParams): void => {
  if (!userInput || !fanData) return;
  const uniqueId = uuidv4();
  const saveData = {
    requestId: `image-${uniqueId}-${imageVersion}`,
    assetType: 'Image' as const,
    query: userInput,
    contributorId: fanData.id,
    contributorDetails: {
      companionId: fanData.companion_id,
      slug: fanData.slug,
      fullName: fanData.given_name || fanData.artist_alias,
      previewImage: fanData.image_url,
      videoDesktopUrl: fanData.video_url,
      videoMobileUrl: fanData.video_url,
    },
    data: {
      url: imageUrl,
      title: `${userInput} Design ${imageVersion}`,
      description: userInput,
    },
  };

  const handleSuccess = (response: any) => {
    if (import.meta.env.DEV) {
      console.log(`Image ${imageVersion} auto-saved successfully`, response);
    }
    if (response?.id) {
      const imageCreationIdRef =
        imageVersion === 1
          ? savedImageCreationId1Ref
          : savedImageCreationId2Ref;
      if (imageCreationIdRef) {
        imageCreationIdRef.current = response.id;
      }
      if (onSaveComplete) {
        onSaveComplete(imageVersion, response.id);
      }
    }
  };

  const handleError = (error: unknown) => {
    if (import.meta.env.DEV) {
      console.error(`Error auto-saving image ${imageVersion}:`, error);
    }
  };

  if (saveCreationAsync && imageVersion === 1) {
    saveCreationAsync(saveData).then(handleSuccess).catch(handleError);
  } else {
    saveCreation(saveData, {
      onSuccess: handleSuccess,
      onError: handleError,
    });
  }
};

interface AutoSaveSongParams {
  songVersion: 1 | 2;
  activeMusicGeneration: GenerationResponse;
  fanData: DemoFan;
  saveCreation: (variables: any, options?: any) => void;
  saveCreationAsync?: (variables: any) => Promise<any>;
  savedSongCreationId1Ref: React.MutableRefObject<string | null>;
  savedSongCreationId2Ref: React.MutableRefObject<string | null>;
  onSaveComplete?: (songVersion: 1 | 2, savedId: string) => void;
  editedLyrics?: string | null;
  editedTitle?: string | null;
}

export const autoSaveSong = ({
  songVersion,
  activeMusicGeneration,
  fanData,
  saveCreation,
  saveCreationAsync,
  savedSongCreationId1Ref,
  savedSongCreationId2Ref,
  onSaveComplete,
  editedLyrics,
  editedTitle,
}: AutoSaveSongParams): void => {
  console.log(`[autoSaveSong] Called for song ${songVersion}`);
  if (!activeMusicGeneration || !fanData) {
    console.log(
      `[autoSaveSong] Early return - missing data for song ${songVersion}`,
    );
    return;
  }

  const track =
    songVersion === 1
      ? {
          url: activeMusicGeneration.songUrl1,
          title: activeMusicGeneration.title1,
          lyrics: activeMusicGeneration.lyrics1,
          lyricsTimestamped: activeMusicGeneration.lyricsTimestamped1,
          duration: activeMusicGeneration.duration1,
        }
      : {
          url: activeMusicGeneration.songUrl2,
          title: activeMusicGeneration.title2,
          lyrics: activeMusicGeneration.lyrics2,
          lyricsTimestamped: activeMusicGeneration.lyricsTimestamped2,
          duration: activeMusicGeneration.duration2,
        };

  console.log(`[autoSaveSong] Song ${songVersion} track.url:`, track.url);
  if (!track.url) {
    console.log(
      `[autoSaveSong] Early return - no track URL for song ${songVersion}`,
    );
    return;
  }

  console.log(
    `[autoSaveSong] Song ${songVersion} past URL check, building saveData...`,
  );

  const albumArt =
    (songVersion === 1
      ? activeMusicGeneration.thumbnailUrl1
      : activeMusicGeneration.thumbnailUrl2) ||
    activeMusicGeneration.musicGptCoverUrl ||
    '';

  const musicGptTaskId = activeMusicGeneration.linkedTaskId;
  const conversionId =
    songVersion === 1
      ? activeMusicGeneration.conversionId1
      : activeMusicGeneration.conversionId2;

  const saveData = {
    requestId: `${activeMusicGeneration.taskId}-${songVersion}`,
    assetType: 'Track' as const,
    query: activeMusicGeneration.prompt,
    contributorId: fanData.id,
    contributorDetails: {
      companionId: fanData.companion_id,
      slug: fanData.slug,
      fullName: fanData.given_name || fanData.artist_alias,
      previewImage: fanData.image_url,
      videoDesktopUrl: fanData.video_url,
      videoMobileUrl: fanData.video_url,
    },
    data: {
      url: track.url,
      previewImage: albumArt,
      albumThumbnail: albumArt,
      genres: [activeMusicGeneration.musicStyle || 'Pop'],
      title: editedTitle?.trim()
        ? editedTitle.trim()
        : track.title
        ? track.title
        : extractTitleFromLyrics(
            track.lyrics || activeMusicGeneration.lyrics,
            3,
            `Version ${songVersion}`,
          ) || `Version ${songVersion}`,
      description: activeMusicGeneration.prompt,
      lyrics:
        editedLyrics || track.lyrics || activeMusicGeneration.lyrics || '',
      lyricsTimestamped: track.lyricsTimestamped || '',
      duration: track.duration || 0,
    },
    // Add music generation metadata for webhook stream URL replacement (new optimized approach)
    ...(musicGptTaskId &&
      conversionId && {
        musicGenerationMetadata: {
          taskId: musicGptTaskId,
          conversionId,
        },
      }),
  };

  console.log(
    `[autoSaveSong] Calling saveCreation for song ${songVersion}`,
    saveData,
  );

  const handleSuccess = (response: any) => {
    console.log(`Song ${songVersion} auto-saved successfully`, response);
    console.log(`Song ${songVersion} response.id:`, response?.id);
    console.log(
      `Song ${songVersion} full response:`,
      JSON.stringify(response, null, 2),
    );
    if (response?.id) {
      const songCreationIdRef =
        songVersion === 1 ? savedSongCreationId1Ref : savedSongCreationId2Ref;
      songCreationIdRef.current = response.id;
      console.log(`Stored song ${songVersion} creation ID: ${response.id}`);

      if (onSaveComplete) {
        onSaveComplete(songVersion, response.id);
      }
    } else {
      console.warn(`No ID in response for song ${songVersion}:`, response);
      console.warn(
        `Song ${songVersion} response keys:`,
        Object.keys(response || {}),
      );
    }
  };

  const handleError = (error: unknown) => {
    console.error(`Error auto-saving song ${songVersion}:`, error);
    console.error(
      `Song ${songVersion} error details:`,
      JSON.stringify(error, null, 2),
    );
  };

  try {
    // Use mutateAsync for Song 1 to avoid callback race condition issues
    if (songVersion === 1 && saveCreationAsync) {
      saveCreationAsync(saveData).then(handleSuccess).catch(handleError);
    } else {
      saveCreation(saveData, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
    }
  } catch (error) {
    console.error(
      `[autoSaveSong] Synchronous error calling saveCreation for song ${songVersion}:`,
      error,
    );
  }
};
